import type {
  BoomHelpFeedbackReason,
  BoomHelpHandoffReason,
  BoomHelpKind
} from "api/boom-help/types";

const BOOM_HELP_SEEN_KEY_PREFIX = "boomHelp.seen";
export const BOOM_HELP_SESSION_KEY_PREFIX = "boomHelp.session";

export const boomHelpSeenKey = (userId: number | undefined) =>
  `${BOOM_HELP_SEEN_KEY_PREFIX}.${userId ?? "anonymous"}`;

export const BOOM_HELP_POLL_INTERVAL_MS = 500;
export const BOOM_HELP_POLL_TIMEOUT_MS = 60_000;
export const BOOM_HELP_MAX_MESSAGE_LENGTH = 4000;

export const ANSWER_TEXT_CLASS =
  "space-y-2 text-sm leading-6 text-content-1000 [overflow-wrap:anywhere] [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5";

export const BOOM_HELP_COPY = {
  title: "Boom Help",
  askTitle: "Ask Boom Help",
  askDescription: "Answers about settings and how Boom works",
  supportTitle: "Message support",
  supportDescription: "Chat with our team",
  empty: "Ask how to set up or find anything in Boom.",
  suggested: "Suggested for this page",
  placeholder: "Ask a question",
  disclaimer: "Boom Help answers from Boom's help pages and can make mistakes.",
  newConversation: "New conversation",
  talkToSupport: "Talk to support",
  close: "Close",
  writing: "Boom Help is writing",
  failed: "Boom Help couldn't answer just now. Try again, or talk to support.",
  retry: "Try again",
  handedOffTitle: "Sent to our support team",
  handedOff:
    "We've sent your question and this chat to our team. You'll get a reply in the support chat.",
  handoffNotSent:
    "We opened a message to our team with your question. Press send to reach a person.",
  helpful: "Helpful",
  notHelpful: "Not helpful",
  feedbackPrompt: "What was wrong?",
  feedbackPlaceholder: "Tell us what was wrong",
  feedbackSend: "Send",
  feedbackThanks: "Thanks, that helps us fix it.",
  tooManyDown: "Sorry this isn't helping. Want to talk to our support team?"
} as const;

export const FEEDBACK_REASONS: {
  value: BoomHelpFeedbackReason;
  label: string;
}[] = [
  { value: "wrong_answer", label: "Wrong answer" },
  { value: "did_not_answer", label: "Didn't answer my question" },
  { value: "screen_not_found", label: "Couldn't find that screen" },
  { value: "other", label: "Something else" }
];

export const HANDOFF_REASON_BY_KIND: Partial<
  Record<BoomHelpKind, BoomHelpHandoffReason>
> = {
  straight_to_support: "straight_to_support",
  unknown: "unknown",
  person_requested: "person_requested",
  not_supported: "not_supported",
  setup: "unresolved_setup",
  explain: "unresolved_setup"
};

const DEFAULT_SUGGESTIONS = [
  "My unit is available in Boom. Why isn't it on Zillow?",
  "How do I add a user and choose what they can see?",
  "Where do I change when Boom creates the guest card in my PMS?"
];

const SUGGESTED_QUESTIONS: { prefix: string; questions: string[] }[] = [
  {
    prefix: "/settings/showings",
    questions: [
      "How do self-guided showings send the lockbox code?",
      "How do I change the showing duration and notice period?",
      "Can leads book back-to-back showings?"
    ]
  },
  {
    prefix: "/settings/integration",
    questions: [
      "When does Boom send an applicant or guest card to my PMS?",
      "Why are only some of my properties in Boom?",
      "Where is Last sync and how do I refresh it?"
    ]
  },
  {
    prefix: "/settings/users",
    questions: [
      "How do I add a user and choose what they can see?",
      "What can a property manager do compared to an admin?",
      "How do I limit a user to some properties?"
    ]
  },
  {
    prefix: "/settings/communication",
    questions: [
      "How do I connect my email to the Inbox?",
      "How do I edit the emails Boom sends to leads?",
      "How do I add an email signature?"
    ]
  },
  {
    prefix: "/settings",
    questions: DEFAULT_SUGGESTIONS
  },
  {
    prefix: "/showings",
    questions: [
      "What do Reserved and Confirmation expired mean?",
      "How do I set up self-guided showings?",
      "How do I link a lockbox to a unit?"
    ]
  },
  {
    prefix: "/lockboxes",
    questions: [
      "How do I link a lockbox to a unit?",
      "Which lockbox brands work with Boom?",
      "How does a self-guided visitor get the lockbox code?"
    ]
  },
  {
    prefix: "/leads",
    questions: [
      "How do I add or rename lead stages?",
      "What does Condition not matched mean on a workflow run?",
      "How do I tag leads?"
    ]
  },
  {
    prefix: "/inbox",
    questions: [
      "Does a long text really split at 160 characters?",
      "Where did Chat go?",
      "How do I start a new conversation with a lead?"
    ]
  },
  {
    prefix: "/listings",
    questions: [
      "My unit is available in Boom. Why isn't it on Zillow?",
      "How do I change the listing photo watermark?",
      "How do I set up my listings page?"
    ]
  },
  {
    prefix: "/units",
    questions: [
      "Why can't I edit the rent on this unit?",
      "What's the difference between Sync with PMS and Edit manually?",
      "How do I turn a unit on for Zillow?"
    ]
  },
  {
    prefix: "/properties",
    questions: [
      "Why can't I edit the rent on this unit?",
      "Why are only some of my properties in Boom?",
      "How do I set entry instructions for a unit?"
    ]
  },
  {
    prefix: "/phone-numbers",
    questions: [
      "How do I choose who a Boom number rings?",
      "How do I send calls to the AI agent?",
      "Can I forward a Boom number to my office line?"
    ]
  },
  {
    prefix: "/workflows",
    questions: [
      "How do I create a workflow?",
      "What does Condition not matched mean?",
      "Why didn't my workflow send?"
    ]
  },
  {
    prefix: "/applications",
    questions: [
      "How do I refund an application fee?",
      "How do I resend the application link?",
      "How do I override a failed identity verification?"
    ]
  },
  {
    prefix: "/verification-templates",
    questions: [
      "How do I set different screening criteria per property?",
      "How do I create a discount code?",
      "Who can change screening settings?"
    ]
  }
];

export const suggestedQuestionsFor = (pathname: string): string[] =>
  SUGGESTED_QUESTIONS.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )?.questions ?? DEFAULT_SUGGESTIONS;
